/**
 * useWhatsAppNotifications — Hook de notifications pour les suggestions WhatsApp urgentes
 *
 * - Polling toutes les 30s via /api/unified-tickets/suggestions/recent-urgent
 * - Anti-spam : 1 notification max / 60s, regroupement si multiples
 * - Son optionnel (désactivé par défaut)
 * - Push navigateur optionnel (demande de permission)
 * - Actions rapides : voir la suggestion / assigner
 */
import { useEffect, useRef, useCallback, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, MessageCircle, Eye, UserPlus } from "lucide-react";
import { enteteTenant } from "../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 30_000;
const THROTTLE_MS = 60_000;
const STORAGE_KEY_SOUND = "wa_notif_sound";
const STORAGE_KEY_PUSH = "wa_notif_push";

const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  const tenant = localStorage.getItem("current_tenant") || "";
  return { Authorization: `Bearer ${token}`, ...enteteTenant(tenant) };
};

// Simple notification sound via Web Audio API
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = "sine";
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
  } catch {
    // Audio not available
  }
}

// Browser push notification
function showBrowserNotification(title, body) {
  if (Notification.permission === "granted") {
    try {
      new Notification(title, {
        body,
        icon: "/favicon.ico",
        tag: "wa-urgent",
        renotify: true,
      });
    } catch {
      // Not supported
    }
  }
}

export default function useWhatsAppNotifications({ onNavigateToTickets, enabled = true }) {
  const lastCheckRef = useRef("");
  const lastNotifTimeRef = useRef(0);
  const seenIdsRef = useRef(new Set());
  const [soundEnabled, setSoundEnabled] = useState(
    () => localStorage.getItem(STORAGE_KEY_SOUND) === "true"
  );
  const [pushEnabled, setPushEnabled] = useState(
    () => localStorage.getItem(STORAGE_KEY_PUSH) === "true"
  );

  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY_SOUND, String(next));
      return next;
    });
  }, []);

  const togglePush = useCallback(async () => {
    if (!pushEnabled && Notification.permission !== "granted") {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") return;
    }
    setPushEnabled((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY_PUSH, String(next));
      return next;
    });
  }, [pushEnabled]);

  const showNotification = useCallback(
    (items) => {
      const now = Date.now();
      if (now - lastNotifTimeRef.current < THROTTLE_MS) return;
      lastNotifTimeRef.current = now;

      // Mark as seen
      items.forEach((i) => seenIdsRef.current.add(i.id));

      const count = items.length;
      const single = count === 1;
      const first = items[0];

      const title = single
        ? first.suggested_title?.replace("[WhatsApp] ", "") || "Alerte terrain"
        : `${count} alertes terrain urgentes`;

      const description = single
        ? (first.suggested_description || "").slice(0, 80)
        : items
            .slice(0, 3)
            .map((i) => (i.suggested_title || "").replace("[WhatsApp] ", ""))
            .join(" / ");

      // Sonner toast with custom JSX
      toast.custom(
        (id) => (
          <div
            data-testid="wa-urgent-toast"
            className="w-[380px] rounded-xl border border-red-500/30 bg-zinc-950 p-4 shadow-2xl shadow-red-900/10"
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <AlertTriangle className="w-4 h-4 text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <MessageCircle className="w-3 h-3 text-emerald-400" />
                  <span className="text-[10px] uppercase tracking-wider text-emerald-400/70 font-medium">
                    WhatsApp urgent
                  </span>
                  {!single && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-bold">
                      {count}
                    </span>
                  )}
                </div>
                <p className="text-sm font-medium text-white truncate">{title}</p>
                <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{description}</p>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    data-testid="wa-toast-view"
                    onClick={() => {
                      toast.dismiss(id);
                      onNavigateToTickets?.();
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 text-xs font-medium text-white hover:bg-white/20 transition-colors"
                  >
                    <Eye className="w-3 h-3" /> Voir
                  </button>
                  <button
                    data-testid="wa-toast-assign"
                    onClick={() => {
                      toast.dismiss(id);
                      onNavigateToTickets?.();
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/20 text-xs font-medium text-red-300 hover:bg-red-500/30 transition-colors"
                  >
                    <UserPlus className="w-3 h-3" /> Assigner
                  </button>
                </div>
              </div>
            </div>
          </div>
        ),
        { duration: 10000, position: "bottom-right" }
      );

      // Optional sound
      if (soundEnabled) playNotificationSound();

      // Optional browser push
      if (pushEnabled) showBrowserNotification(title, description);
    },
    [onNavigateToTickets, soundEnabled, pushEnabled]
  );

  // Polling
  useEffect(() => {
    if (!enabled) return;

    const poll = async () => {
      try {
        const since = lastCheckRef.current || "";
        const url = `${API}/api/unified-tickets/suggestions/recent-urgent?source=whatsapp${since ? `&since=${encodeURIComponent(since)}` : ""}`;
        const res = await fetch(url, { headers: getAuthHeaders() });
        if (!res.ok) return;
        const data = await res.json();

        // Update timestamp to now
        lastCheckRef.current = new Date().toISOString();

        // Filter out already-seen items
        const newItems = (data.items || []).filter(
          (i) => !seenIdsRef.current.has(i.id)
        );

        if (newItems.length > 0) {
          showNotification(newItems);
        }
      } catch {
        // Silently ignore polling errors
      }
    };

    // Initial check after 3s (let the page load)
    const initTimer = setTimeout(poll, 3000);
    const interval = setInterval(poll, POLL_INTERVAL);

    return () => {
      clearTimeout(initTimer);
      clearInterval(interval);
    };
  }, [enabled, showNotification]);

  return { soundEnabled, pushEnabled, toggleSound, togglePush };
}
